/**
 * 需要对图表option进行的额外处理方法
 */
import { ChartOption, IChartConfig, IEChartsLabelLayoutParams, IEChartsTooltipParams, IPlugConfig } from '../_config/_interface';
import type { EChartsType } from 'echarts/core';
import type {
  IEchartsAxisLabel,
  IEchartsOption,
  IChartXLabelConfig,
} from './types';
import { formatNumberByComma } from '@/pages/datalab/utils/utils';
import { isObject, merge } from 'lodash-es';
import { DEFAULT_RICH_CONFIG, REVERSE_POSITION_MAP } from '../_config/_constant';

/**
 * 根据给定的配置对 option 的 x 轴标签进行对齐与倾斜优化。
 *
 * 当 config.type 为 'horizontal' 时，强制水平居中显示；
 * 否则根据数据量与字符长度自动判断倾斜展示，以提升可读性与避免遮挡。
 *
 * @param option - ECharts 配置对象（仅使用到 xAxis 与 tooltip.axisPointer.label.show）
 * @param config - 文本方向配置，type='horizontal' 时强制水平居中，其他情况下自动判断
 * @returns 更新后的 ECharts 配置对象（保持不可变，返回新对象或原对象）
 */
export const setXLabelAlign = (
  option: IEchartsOption,
  config?: IChartXLabelConfig,
): IEchartsOption => {
  if (!option.xAxis) return option;

  // 判断是否需要倾斜
  const isItalicAuto = (() => {
    // 当用户设置文本水平时, 设置x轴文本居中展示
    if (config?.type === 'horizontal') return false;
    const data = option?.xAxis?.data || [];
    // 当用户设置文本 Auto放置的时候, 需要自动计算是倾斜还是水平
    // 当x轴文本个数大于等于10个时, 文本倾斜
    if (data.length >= 10) return true;
    return (data as Array<string | { length?: number; }>).some(
      (t) => ((t as { length?: number; })?.length || 0) >= 10,
    );
  })();

  const desired: { axisLabel: IEchartsAxisLabel; } = isItalicAuto
    ? {
      axisLabel: {
        rotate: 30,
        margin: 15,
        hideOverlap: true,
        align: 'right',
        color: '#838697',
        fontSize: 10,
      },
    }
    : { axisLabel: { align: 'center', color: '#838697', fontSize: 10 } };

  // 对比option.xAxis.axisLabel 旧数据 和 desired.axisLabel 新数据, 如果相同, 则直接返回option
  // if (desired.axisLabel.align === option.xAxis.axisLabel?.align) {
  //   return option;
  // }
  // x轴文本倾斜摆放时, 没有高亮显示,
  //  newOption.tooltip.axisPointer.label && (newOption.tooltip.axisPointer.label.show = false);
  // 否则返回新的对象option
  const newOption: IEchartsOption = {
    ...option,
    xAxis: {
      ...option.xAxis,
      axisLabel: desired.axisLabel,
    },
  };
  // x轴文本倾斜摆放时, 没有高亮显示,
  if (newOption.tooltip?.axisPointer?.label) {
    newOption.tooltip.axisPointer.label.show = !isItalicAuto;
  }
  return newOption;
};

/**
 * 设置横向条形图的坐标轴名称
 * @param option - ECharts配置选项对象
 * @param chartConfig - 图表配置对象，包含x轴和y轴的名称设置
 * @returns 更新后的配置
 */
export const setHorizontalBarAxisName = (option: ChartOption, chartConfig: IChartConfig) => {
  // 处理x轴名称设置
  if (chartConfig?.xAxisName && Array.isArray(option?.xAxis) && option?.xAxis?.[0]) {
    if (option.grid && typeof option.grid === 'object' && !Array.isArray(option.grid) && option.grid.bottom) {
      // 设置x轴名称后，调整图表底部间距
      const currentBottom = typeof option.grid.bottom === 'number' ? option.grid.bottom : parseInt(String(option.grid.bottom), 10) || 0;
      option.grid.bottom = currentBottom + 12;
    }
    // 相对于x轴居中，需求相对于图形居中，使用dom元素定位解决
    // 以下是注释掉的代码，用于展示原始实现方式
    // option.xAxis[0] = {
    //   ...option.xAxis[0],
    //   name: chartConfig.xAxisName,
    //   nameLocation: 'center',
    //   nameTextStyle: {
    //     color: '#838697',
    //     fontSize: 12,
    //   },
    // };
  }
  // 处理y轴名称设置
  // if (chartConfig?.yAxisName && option?.yAxis) {
  //   if (option.grid && typeof option.grid === 'object' && !Array.isArray(option.grid) && option.grid.top) {
  //     // 设置y轴名称后，调整图表顶部间距
  //     const currentTop = typeof option.grid.top === 'number' ? option.grid.top : parseInt(String(option.grid.top)) || 0;
  //     option.grid.top = currentTop + 20;
  //   }
  //   // 配置y轴名称及其样式
  //   option.yAxis = {
  //     ...option.yAxis,
  //     name: chartConfig.yAxisName,
  //     nameLocation: 'start',
  //     nameTextStyle: {
  //       color: '#838697',
  //       fontSize: 12,
  //       align: 'center'
  //     },
  //   };
  // }
  return option;
};

/**
 * 根据 series 中最长的数据字符串长度，为 grid.right 动态增加偏移量
 * @param option - ECharts 配置对象
 * @param chartConfig - 图表配置对象，包含 showValues,showValuesIndexs 属性
 * @param ecInstance - ECharts 实例对象
 * @param unit - 单字符宽度换算像素（经验值）
 * @param minRight - 最小右侧留白
 * @param maxRight - 最大右侧留白
 * @param farThreshold - 最大值与轴范围的比例阈值，超过此值则不偏移
 */
export const adjustGridRightBySeries = (
  option: ChartOption,
  chartConfig?: IChartConfig,
  ecInstance?: EChartsType | null,
  unit = 6,
  minRight = 10,
  maxRight = 140,
  farThreshold = 0.15,
) => {
  const { labelContent } = chartConfig || {};
  // 未开启数值展示则不偏移(由调用方根据 showDataLabel 判断)
  // if (!showDataLabel) return;
  const series = (option as any)?.series as any[] | undefined;
  if (!Array.isArray(series)) return;

  // 仅统计需要展示数值的系列（showValuesIndexs 指定的下标）
  // let indexSet: Set<number> | undefined;
  // if (Array.isArray(chartConfig?.showValuesIndexs) && chartConfig!.showValuesIndexs!.length > 0) {
  //   const normalized = (chartConfig!.showValuesIndexs! as Array<number | string>)
  //     .map((i) => typeof i === 'number' ? i : Number(i))
  //     .filter((i) => Number.isFinite(i) && i >= 0) as number[];
  //   if (normalized.length > 0) indexSet = new Set(normalized);
  // }

  // 计算当前图中显示的最大值
  let maxValue = 0;
  // eslint-disable-next-line @typescript-eslint/prefer-for-of
  for (let idx = 0;idx < series.length;idx += 1) {
    // if (indexSet && !indexSet.has(idx)) continue;
    const s = series[idx];
    const dataArr = (s && Array.isArray(s.data)) ? s.data : [];
    for (const v of dataArr) {
      const raw = (v && typeof v === 'object' && 'value' in v) ? (v as any).value : v;
      const num = Number(raw);
      if (!Number.isNaN(num) && num > maxValue) maxValue = num;
    }
  }

  // 优先通过 ECharts 实例读取当前坐标轴实际范围（不做推断）
  let axisMax: number | undefined;
  try {
    const extent = (ecInstance as any)?.getModel?.()?.getComponent?.('xAxis', 0)?.axis?.scale?.getExtent?.();
    if (Array.isArray(extent) && typeof extent[1] === 'number') {
      const [, max] = extent;
      axisMax = max;
    }
  } catch { }
  if (axisMax === null || axisMax === undefined) return; // 无法获取轴范围则不偏移
  // 若显示值距离最大值很远（留白较大），则不做额外右侧偏移
  if (axisMax > 0 && maxValue <= axisMax * (1 - farThreshold)) return;
  // 计算最长文本长度（用于像素估算）
  let maxLen = 0;
  // eslint-disable-next-line @typescript-eslint/prefer-for-of
  for (let idx = 0;idx < series.length;idx += 1) {
    // if (indexSet && !indexSet.has(idx)) continue;
    const s = series[idx];
    const dataArr = (s && Array.isArray(s.data)) ? s.data : [];
    for (const v of dataArr) {
      const raw = (v && typeof v === 'object' && 'value' in v) ? (v as any).value : v;
      // 数据标签展示是否包含维度信息的展示
      const haveDimension = labelContent?.includes('dimension');
      const str = haveDimension ? (s?.name ?? '') + String(raw ?? '') : String(raw ?? '');
      if (str.length > maxLen) maxLen = str.length;
    }
  }
  const extra = Math.min(maxRight, Math.max(minRight, Math.ceil(maxLen * unit)));
  const grid = (option as any).grid || ((option as any).grid = {});
  if (typeof grid.right === 'number') {
    grid.right = grid.right + extra;
  } else if (grid.right === null || grid.right === undefined) {
    grid.right = extra;
  }
};

/**
 * 设置水平条形图的缩放功能
 * @param option - ECharts配置项对象
 * @param endValue - 数据窗口范围的结束数值，默认为19
 * @returns 更新后的配置
 */
export const setHorizontalBarDataZoom = (option: ChartOption, endValue = 19) => {
  if (option.grid && typeof option.grid === 'object' && !Array.isArray(option.grid) && option.grid.right) {
    // 设置图表的grid属性，调整右侧间距
    const currentRight = typeof option.grid.right === 'number' ? option.grid.right : parseInt(String(option.grid.right), 10) || 0;
    option.grid.right = currentRight + 10;
  }
  // 配置数据缩放组件，包含inside和slider两种类型
  option.dataZoom = [
    {
      type: 'inside', // 内置型数据区域缩放组件
      yAxisIndex: 0, // y轴索引
      startValue: 0, // 数据窗口范围的起始数值
      endValue, // 数据窗口范围的结束数值
      // zoomLock: true, // 锁定窗口大小
      zoomOnMouseWheel: false, // 表示鼠标滚轮不能触发缩放
      moveOnMouseWheel: true, // 表示不按任何功能键，鼠标移动能触发数据窗口平移
      moveOnMouseMove: false, // 表示鼠标移动不能触发平移
    },
    {
      type: 'slider', // 滑动条型数据区域缩放组件
      yAxisIndex: 0, // y轴索引
      orient: 'vertical', // 滑动条方向为垂直
      right: 0, // 距离右侧的距离
      width: 14, // 滑动条的宽度
      handleSize: '80%', // 滑动条手柄的大小
      startValue: 0, // 数据窗口范围的起始数值
      endValue, // 数据窗口范围的结束数值
      zoomLock: true, // 锁定窗口大小，防止缩放
      showDetail: false, // 不显示拖拽时的详细信息文本
    }
  ];
  return option;
};

// 图表数据中series.data数组中的元素 不是object的时候返回 {value}
export const getValueObject = (value: any) => isObject(value) ? value : { value };

/**
 * 设置首尾标签的label展示
 * @param option ECharts配置项对象
 * @param chartConfig 图表配置
 * @param config 配置项
 * @returns 更新后的配置
 */
export const setFirstLastLabel = (
  option: ChartOption,
  _chartConfig?: IChartConfig,
  _config?: IPlugConfig
) => {
  // const { } = chartConfig || {};
  // const { } = config || {};
  if (Array.isArray(option?.series)) {
    option.series = (option?.series || [])?.map((temp: any) => {
      // 最后一个点 设置label 的 align 避免遮盖
      const lastData = getValueObject(temp?.data?.[temp?.data?.length - 1]);
      temp.data[temp.data.length - 1] = merge(lastData, { label: { ...(lastData?.label ?? {}), align: 'right' } });
      // 第一个点 设置label 的 align 避免遮盖
      const firstData = getValueObject(temp?.data?.[0]);
      temp.data[0] = merge(firstData, { label: { ...(firstData?.label ?? {}), align: 'left' } });
      return temp;
    });
  }
  return option;
};

/**
 * 调整图表边距，防止标签溢出被截取
 * @param option 图表配置选项
 * @param chartConfig 图表配置
 * @param config 配置项
 */
export const adjustChartMargin = (
  option: ChartOption,
  chartConfig?: IChartConfig,
  config?: IPlugConfig,
) => {
  const { showDataLabel, labelContent } = chartConfig || {};
  const { ecInstance, farThreshold = 0.15, topAuto = false, maxTop = 40, rightAuto = false, maxRight = 60 } = config || {};
  // 不显示标签或series非数组不计算
  if (!Array.isArray(option?.series) || !showDataLabel) return;
  // 适配偏移，防止标签溢出被截取
  if (rightAuto
    && Array.isArray(option?.series)
    && Array.isArray(option?.series?.[0]?.data)
    && option?.series?.[0]?.data?.length >= 14
    && option.grid
    && typeof option.grid === 'object'
    && !Array.isArray(option.grid)
    && option.grid.right
    && !option.grid.rightAuto
  ) {
    // 最后一个有数据的series
    const lastSeries = option?.series?.find(item => getValueObject(item?.data?.[item?.data?.length - 1])?.value);
    const lastData = getValueObject(lastSeries?.data?.[lastSeries?.data?.length - 1]);
    // 设置图表的grid属性，调整右侧间距
    const marginRight = lastData?.value?.length * 4 || 0;
    if (lastData?.value?.length > 8) {
      const currentRight = typeof option.grid.right === 'number' ? option.grid.right : parseInt(String(option.grid.right), 10) || 0;
      option.grid.right = currentRight + marginRight > maxRight ? maxRight : currentRight + marginRight;
      // 添加右侧边距调整标识，防止多次调整
      option.grid.rightAuto = true;
    };
  };
  // 开启顶部自适应和设置了标签内容才计算偏移
  if (topAuto && labelContent?.length && ecInstance) {
    // 优先通过 ECharts 实例读取当前坐标轴实际范围（不做推断）
    let axisMax: number | undefined;
    try {
      const extent = (ecInstance as any)?.getModel?.()?.getComponent?.('yAxis', 0)?.axis?.scale?.getExtent?.();
      if (Array.isArray(extent) && typeof extent[1] === 'number') {
        const [, max] = extent;
        axisMax = max;
      }
    } catch { }
    // 无法获取轴范围则不偏移
    if (axisMax === null || axisMax === undefined) return;
    // 计算数据最大值
    const dataList: any[] = [];
    option?.series?.forEach(seriesItem => {
      seriesItem?.data?.forEach(item => {
        dataList?.push(item?.value || item);
      });
    });
    const maxValue = Math.max(...dataList);
    // 若显示值距离最大值很远（留白较大），则不做额外偏移
    if (axisMax > 0 && maxValue <= axisMax * (1 - farThreshold)) return;
    if (topAuto
      && option.grid
      && typeof option.grid === 'object'
      && !Array.isArray(option.grid)
      && option.grid.top
      && !option.grid.topAuto
    ) {
      // 设置图表的grid属性，调整顶部间距
      const marginTop = labelContent?.length * 10;
      const currentTop = typeof option.grid.top === 'number' ? option.grid.top : parseInt(String(option.grid.top), 10) || 0;
      option.grid.top = currentTop + marginTop > maxTop ? maxTop : currentTop + marginTop;
      // 添加顶部间距调整标识，防止多次调整
      option.grid.topAuto = true;
    }
  }
};

/**
 * 格式化标签内容
 * @param params echarts返回
 * @param chartConfig 图表配置选项
 * @param config 图表配置
 * @param valueType 数据类型
 * @returns 标签展示内容
 */
export const formatLabelContent = (
  params: IEChartsTooltipParams,
  chartConfig?: IChartConfig,
  config?: IPlugConfig,
  valueType?: string,
) => {
  const {
    labelContent
  } = chartConfig || {};
  const {
    isNewline = true,
    filterZero = false,
    filterEmpty = false,
    dimensionFormatter,
    metricFormatter,
    percentFormatter,
  } = config || {};
  // 没有标签配置或过滤空值或0值
  if (!labelContent?.length || (filterZero && !params?.value) || (filterEmpty && params?.value === "")) {
    return "";
  }
  // 判断是否百分比指标
  const isPercent = valueType === 'percent' || params?.data?.valueIsPercent;
  // 数值格式化
  const metricValue = params?.value ? formatNumberByComma(params?.value) : '-';
  // 维度最终展示
  const dimension = dimensionFormatter ? dimensionFormatter(params) : params?.seriesName || '';
  // 指标最终展示
  // eslint-disable-next-line no-nested-ternary
  const metric = metricFormatter ? metricFormatter(params, isPercent) : (isPercent ? `${metricValue}%` : metricValue);
  const percent = percentFormatter ? percentFormatter(params) : params?.data?.percent || '';
  // 拼接标签内容（根据配置包含维度/指标）
  const separator = isNewline ? '\n' : ': ';
  let content = '';
  const haveDimension = labelContent?.includes('dimension');
  const haveMetric = labelContent?.includes('metric');
  const havePercent = labelContent?.includes('percent');
  // 标签内容展示格式(从上到下：Dimension、Metric、Percentage)
  if (haveDimension) {
    const haveSeparator = haveMetric || havePercent;
    content += `{dimension|${dimension}}${haveSeparator ? separator : ''}`;
  }
  if (haveMetric) {
    const haveSeparator = havePercent;
    content += `{metric|${metric}}${haveSeparator ? separator : ''}`;
  }
  if (havePercent) {
    content += `{percent|${percent}}`;
  }
  return content;
};

/**
 * 生成标签布局配置（处理标签显示/隐藏逻辑）
 * @param params echarts返回
 * @param chartConfig 图表配置选项
 * @param _config 图表配置
 * @returns 标签布局配置
 */
export const getLabelLayout = (
  params: IEChartsLabelLayoutParams,
  chartConfig?: IChartConfig,
  _config?: IPlugConfig,
) => {
  const {
    displayForm
  } = chartConfig || {};
  const { labelRect, rect } = params;
  // 当柱状图的柱子高度大于文本高度才显示标签
  const isShow = rect && labelRect && (rect?.height > labelRect?.height);
  if (!isShow) {
    return {
      // 可设置fontSize为0，因为echarts配置兜底，标签还是会展示出来
      // 结合label中overflow: 'truncate'达到隐藏标签的功能
      width: 0,
      hideOverlap: displayForm === 'adaptive',
    };
  }
  return {
    hideOverlap: displayForm === 'adaptive',
  };
};

/**
 * 处理负数数据标签（负数标签位置反转）
 * @param data 数据
 * @param label 标签配置
 * @param reverseMap 反转位置映射
 * @returns 新数据
 */
export const handleReverseData = (
  data: (string | number)[],
  label: IPlugConfig["label"],
  reverseMap: Record<string, string>
) => {
  const newData = data?.map((v: number | string) => {
    const valueObject = getValueObject(v);
    const isReverse = valueObject?.value < 0;
    // 计算反转后的位置和对齐方式
    const reversedPosition = label?.resversePosition || reverseMap?.[label?.position] || label?.position;
    const reversedAlign = label?.resverseAlign || reverseMap?.[label?.align] || label?.align;
    const newLabel = {
      ...label,
      position: isReverse ? reversedPosition : label?.position,
      align: isReverse ? reversedAlign : label?.align
    };
    return merge(valueObject, { label: newLabel });
  });
  return newData;
};

/**
 * 设置数据标签功能
 * @param option ECharts配置项对象
 * @param chartConfig 图表配置
 * @param config 配置项
 * @returns 更新后的配置
 */
export const setDataLabel = (
  option: ChartOption,
  chartConfig?: IChartConfig,
  config?: IPlugConfig
) => {
  const {
    showDataLabel,
    displayForm,
  } = chartConfig || {};
  const {
    label: configLabel = {},
    labelAuto = false,
    isLabelVisible = true,
    filterZero = false,
    filterEmpty = false,
  } = config || {};
  // 自适应展示标签
  if (displayForm) {
    option.labelLayout = {
      ...(option?.labelLayout || {}),
      hideOverlap: displayForm === 'adaptive', // 隐藏重叠的标签
    };
  }
  if (!showDataLabel) return option;
  // 首尾标签偏移自适应(之前防止标签被截取，adjustChartMargin方法计算边距后可不设置首尾标签偏移)
  if (labelAuto
    && Array.isArray(option?.series)
    && Array.isArray(option?.series?.[0]?.data)
    && option?.series?.[0]?.data?.length > 14
  ) {
    option = setFirstLastLabel(option, chartConfig);
  }
  // 设置数据标签的展示内容
  if (Array.isArray(option?.series)) {
    option.series = (option?.series || [])?.map((seriesItem: any) => {
      const { valueType, data, type, itemStyle } = seriesItem;
      // 数据标签配置
      const baseLabel: IPlugConfig["label"] = {
        show: true,
        position: 'top',
        align: 'center',
        overflow: 'truncate',
        formatter: (params: IEChartsTooltipParams) => formatLabelContent(params, chartConfig, config, valueType),
        // 富文本配置
        rich: {
          metric: DEFAULT_RICH_CONFIG,
          dimension: DEFAULT_RICH_CONFIG,
          total: DEFAULT_RICH_CONFIG,
          percent: DEFAULT_RICH_CONFIG,
          ...(configLabel?.rich) // 允许覆盖富文本配置
        },
        ...configLabel, // 其他label配置
      };
      // 柱状图和开启高度判断（横向柱状图不需要该功能通过外部isLabelVisible传入控制）
      const shouldApplyBarLayout = displayForm === 'adaptive' && isLabelVisible && type === 'bar';
      // 数值为负数是否反向
      const isResverseData = data?.some((v: number | string) => getValueObject(v)?.value < 0);
      // 是否需要设置每个数据的标签label（如果需要反转或者过滤值，则需要设置每个数据的label）
      const needPerDataLabel = isResverseData || filterZero || filterEmpty;
      if (needPerDataLabel) {
        // 需要反转设置series的data的单独label
        const newData = handleReverseData(data, baseLabel, REVERSE_POSITION_MAP);
        return {
          ...seriesItem,
          // 折线图的圆点默认隐藏，需要展示出来
          ...(showDataLabel && itemStyle ? {
            itemStyle: {
              ...(itemStyle || {}),
              opacity: 1,
            }
          } : {}),
          data: newData,
          labelLayout: shouldApplyBarLayout ? (params: IEChartsLabelLayoutParams) => getLabelLayout(params, chartConfig, config) : undefined, // shouldApplyBarLayout 为 false 直接使用option.labelLayout设置即可
        };
      }
      // 非单独配置场景：直接设置系列级label
      return {
        ...seriesItem,
        // 折线图的圆点默认隐藏，需要展示出来
        ...(showDataLabel && itemStyle ? {
          itemStyle: {
            ...(itemStyle || {}),
            opacity: 1,
          }
        } : {}),
        label: baseLabel,
        labelLayout: shouldApplyBarLayout ? (params: IEChartsLabelLayoutParams) => getLabelLayout(params, chartConfig, config) : undefined, // shouldApplyBarLayout 为 false, 直接使用option.labelLayout设置即可
      };
    });
  }
  return option;
};